// Category tree management page — parameterised by category type.
// Admin-only: manages the category hierarchy for a given domain (control/event/action/framework).
// Live editing — no draft/activate cycle. Mutations hit the API directly.
//
// Mirrors the taxonomy DraftTreeEditor UX: collapsible tree with context menus
// for add child, rename, and delete.

import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  categoriesApi,
  buildCategoryTree,
  categoryTypeLabels,
  categoryObjectLabels,
  type CategoryType,
  type CategoryTreeNode,
} from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { useToast } from '@/lib/hooks/use-toast'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog'
import { Trash2 } from 'lucide-react'
import {
  CategoryTreeEditor,
  AddCategoryDialog,
  EditCategoryDialog,
} from '@/components/features/category-tree'

interface CategoryPageProps {
  categoryType: CategoryType
}

export function CategoryPage({ categoryType }: CategoryPageProps) {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const catApi = categoriesApi(api, categoryType)

  const typeLabel = categoryTypeLabels[categoryType]
  const objectLabel = categoryObjectLabels[categoryType]

  // --- State for dialogs ---
  const [addTarget, setAddTarget] = useState<CategoryTreeNode | null>(null)
  const [editTarget, setEditTarget] = useState<CategoryTreeNode | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CategoryTreeNode | null>(null)

  // --- Query: load tree ---
  const treeQuery = useQuery({
    queryKey: queryKeys.categories.tree(slug, categoryType),
    queryFn: () => catApi.tree(),
  })

  const tree = treeQuery.data ? buildCategoryTree(treeQuery.data) : []
  const nodeCount = treeQuery.data?.length ?? 0

  const invalidateTree = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.categories.all(slug, categoryType),
    })
  }, [queryClient, slug, categoryType])

  // --- Mutations ---

  const createMutation = useMutation({
    mutationFn: (params: { parentId: string; name: string; description: string; ownerRoleId: string }) =>
      catApi.create({
        parent_id: params.parentId,
        name: params.name,
        description: params.description || undefined,
        owner_role_id: params.ownerRoleId,
      }),
    onSuccess: (created) => {
      invalidateTree()
      setAddTarget(null)
      toast({ title: `Created "${created.name}"` })
    },
    onError: (err) => {
      toast({
        title: 'Failed to create category',
        description: err instanceof ApiRequestError ? err.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (params: { id: string; name: string; description: string; ownerRoleId: string }) =>
      catApi.update(params.id, {
        name: params.name,
        description: params.description || undefined,
        owner_role_id: params.ownerRoleId,
      }),
    onSuccess: (updated) => {
      invalidateTree()
      setEditTarget(null)
      toast({ title: `Updated "${updated.name}"` })
    },
    onError: (err) => {
      toast({
        title: 'Failed to update category',
        description: err instanceof ApiRequestError ? err.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => catApi.delete(id),
    onSuccess: () => {
      invalidateTree()
      const name = deleteTarget?.name
      setDeleteTarget(null)
      toast({ title: `Deleted "${name}"` })
    },
    onError: (err) => {
      setDeleteTarget(null)
      toast({
        title: 'Failed to delete category',
        description: err instanceof ApiRequestError ? err.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })

  // --- Handlers ---

  const handleAddChild = useCallback((node: CategoryTreeNode) => {
    setAddTarget(node)
  }, [])

  const handleAddConfirm = useCallback(
    (name: string, description: string, ownerRoleId: string) => {
      if (!addTarget) return
      createMutation.mutate({
        parentId: addTarget.id,
        name,
        description,
        ownerRoleId,
      })
    },
    [addTarget, createMutation],
  )

  const handleEdit = useCallback((node: CategoryTreeNode) => {
    setEditTarget(node)
  }, [])

  const handleEditConfirm = useCallback(
    (name: string, description: string, ownerRoleId: string) => {
      if (!editTarget) return
      updateMutation.mutate({ id: editTarget.id, name, description, ownerRoleId })
    },
    [editTarget, updateMutation],
  )

  const handleDelete = useCallback((node: CategoryTreeNode) => {
    setDeleteTarget(node)
  }, [])

  // --- Loading/error ---

  if (treeQuery.isLoading) {
    return <TableSkeleton />
  }

  if (treeQuery.error) {
    return (
      <ErrorState
        message={`Failed to load ${typeLabel.toLowerCase()} categories`}
        technicalDetail={
          treeQuery.error instanceof ApiRequestError
            ? treeQuery.error.message
            : treeQuery.error.message
        }
        onRetry={() => treeQuery.refetch()}
      />
    )
  }

  return (
    <div className="max-w-4xl space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold">{typeLabel} Categories</h1>
        <p className="text-muted-foreground">
          Manage the {typeLabel.toLowerCase()} category hierarchy. Categories organise{' '}
          {objectLabel} and control who has access to them.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {nodeCount} {nodeCount === 1 ? 'category' : 'categories'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CategoryTreeEditor
            roots={tree}
            categoryType={categoryType}
            onAddChild={handleAddChild}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      {/* Add Category Dialog */}
      <AddCategoryDialog
        open={addTarget !== null}
        parentName={addTarget?.name ?? ''}
        onConfirm={handleAddConfirm}
        onCancel={() => setAddTarget(null)}
        isPending={createMutation.isPending}
      />

      {/* Edit Dialog */}
      <EditCategoryDialog
        open={editTarget !== null}
        currentName={editTarget?.name ?? ''}
        currentDescription={editTarget?.description ?? ''}
        currentOwnerRoleId={editTarget?.owner_role_id ?? ''}
        onConfirm={handleEditConfirm}
        onCancel={() => setEditTarget(null)}
        isPending={updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This category will be permanently deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
